// src/contexts/AuthContext.tsx
import React, { createContext, useState, ReactNode } from 'react';
import { AuthContextType, AuthState, UserSession } from '../types/auth';
import authService from '../services/authService';
import { useNavigate } from 'react-router-dom';


export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true, // Initially true while we check for a token
  });

  const fetchUser = async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    const token = authService.getToken();
    if (token) {
      try {
        const userProfile = await authService.fetchMe();

        // Check if user has admin access (case-insensitive check)
        const userRole = userProfile.role?.toLowerCase().replace(' ', '_');
        const allowedRoles = ['admin', 'super_admin', 'superadmin'];

        if (!allowedRoles.includes(userRole)) {
          // User doesn't have admin access - logout
          authService.logout();
          localStorage.removeItem('user_profile');
          setAuthState({ isAuthenticated: false, user: null, isLoading: false });
          return;
        }

        // User has admin access
        localStorage.setItem('user_profile', JSON.stringify(userProfile));
        setAuthState({
          isAuthenticated: true,
          user: userProfile,
          isLoading: false,
        });
      } catch (error) {
        // Error handled by notification system
        authService.logout(); // Clear token if it's invalid
        setAuthState({ isAuthenticated: false, user: null, isLoading: false });
        // Don't redirect - let the user stay on current page (likely landing page)
      }
    } else {
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
    }
  };

  const login = async (email: string, password: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.login({ email, password });

      // Fetch user profile to check role
      const userProfile = await authService.fetchMe();

      // Check if user has admin access (case-insensitive check)
      const userRole = userProfile.role?.toLowerCase().replace(' ', '_');
      const allowedRoles = ['admin', 'super_admin', 'superadmin'];

      if (!allowedRoles.includes(userRole)) {
        // User doesn't have admin access - logout and throw error
        authService.logout();
        setAuthState({ isAuthenticated: false, user: null, isLoading: false });
        throw new Error('Accès refusé. Seuls les administrateurs peuvent accéder à cette plateforme.');
      }

      // User has admin access - save profile and proceed
      localStorage.setItem('user_profile', JSON.stringify(userProfile));
      setAuthState({
        isAuthenticated: true,
        user: userProfile,
        isLoading: false,
      });
      navigate('/dashboard'); // Navigate to dashboard after login
    } catch (error) {
      // Error handled by notification system
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      throw error; // Re-throw to be caught by the login page
    }
  };

  const register = async (email: string, password: string, full_name: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.register({ email, password, full_name });
      setAuthState((prev) => ({ ...prev, isLoading: false })); // Not logging in after register for now
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error; // Re-throw to be caught by the register page
    }
  };

  const sendOtp = async (email: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.sendOtp({ email });
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const verifyOtp = async (email: string, otp: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.verifyOtp({ email, otp });
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const deleteAccount = async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.deleteAccount();
      authService.logout(); // Clear token and local state after deletion
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      navigate('/'); // Redirect to landing page
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem('user_profile'); // Clear profile from localStorage
    setAuthState({ isAuthenticated: false, user: null, isLoading: false });
    navigate('/'); // Redirect to landing page
  };

  const logoutAll = async () => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.logoutAll();
      localStorage.removeItem('user_profile'); // Clear profile from localStorage
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      navigate('/'); // Redirect to landing page after logging out from all devices
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const getUserSessions = async (): Promise<UserSession[]> => {
    // Ne pas modifier authState.isLoading car Settings.tsx a son propre état loadingSessions
    try {
      const sessions = await authService.getUserSessions();
      return sessions;
    } catch (error) {
      // Error handled by notification system
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.forgotPassword({ email });
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const verifyResetCode = async (email: string, resetCode: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.verifyResetCode({ email, reset_code: resetCode });
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const resetPassword = async (email: string, resetCode: string, newPassword: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      await authService.resetPassword({ email, reset_code: resetCode, new_password: newPassword });
      setAuthState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      // Error handled by notification system
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  // Check for token and fetch user on initial load
  React.useEffect(() => {
    fetchUser();
    // Also try to load user profile from localStorage if token is present but user state is null
    const token = authService.getToken();
    if (!authState.user && token) {
      try {
        const storedProfile = localStorage.getItem('user_profile');
        if (storedProfile) {
          setAuthState((prev) => ({
            ...prev,
            isAuthenticated: true,
            user: JSON.parse(storedProfile),
            isLoading: false,
          }));
        }
      } catch (error) {
        // Error handled by notification system
        localStorage.removeItem('user_profile');
      }
    }
  }, []); // Run only once on mount

  return (
    <AuthContext.Provider value={{ authState, login, register, logout, fetchUser, sendOtp, verifyOtp, deleteAccount, logoutAll, getUserSessions, forgotPassword, verifyResetCode, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

